import { defineComponent as _defineComponent } from 'vue'
import BCXForm from '@/components/molecules/forms/BCXForm.vue';
import Vue, {
  computed, PropType, ref, watch
} from 'vue';
import { BcxFormField, BcxFormFieldErrorMessage } from '@/models/BcxForm';
import useAddressTools from '@/mixins/useAddressTools';
import { asyncComputed, useVModel } from '@vueuse/core';
import { ComponentAddressData, ValidateInputs } from '@/models/Invoice';
import { SelectItem } from '@/models/SelectItem';
import { City } from '@/models/Geo';
import useInvoice from '@/views/my-projects/composables/useInvoice';
import useUser from '@/mixins/useUser';
import { i18nToItemList } from '@/utils/selectItemTools';

type InvoiceEditorFormField = BcxFormField & {
  name: keyof ComponentAddressData;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoiceAddressDataEditor',
  props: {
  type: {
    type: String as PropType<'sender' | 'recipient'>,
    required: true,
  },
  addressData: {
    type: Object as PropType<ComponentAddressData>,
    required: true
  },
  errorMessages: {
    type: Object as PropType<Record<string, BcxFormFieldErrorMessage>>,
    default: () => ({})
  }
},
  setup(__props, { expose }) {

const props = __props;



const { tUserFallback: t } = useUser('self');
const { countries } = useAddressTools();
const { getCitiesByPostCodeOrCity, findChangedProps } = useInvoice();

const citiesList = ref<City[]>([]);
const addressDataVM = useVModel(props, 'addressData');
const errorMessagesVM = useVModel(props, 'errorMessages');

/**
 * Fetches a list of cities by postal code (or city name) and updates the reactive citiesList.
 * If the API call returns a valid (truthy) response, the result is assigned to citiesList.value.
 * Otherwise, citiesList.value is not updated.
 *
 * @param zipOrCitySearchString - The postal code or city string to search for.
 */
async function getCitiesList(zipOrCitySearchString: string) {
  await getCitiesByPostCodeOrCity(addressDataVM.value.countryIsoCode, zipOrCitySearchString).then((response) => {
    if (response) {
      citiesList.value = response;
    }
  });
}

/**
 * Formats a place name and postal code into a single string.
 * @param placeName - The name of the place.
 * @param postalCode - The postal code of the place.
 * @returns A formatted string combining postal code and place name.
 */
function formatPlaceName(placeName: string, postalCode: string) {
  return `${postalCode}, ${placeName.split(',')[0]?.trim() ?? placeName}`;
}

const countriesOptions = computed(() => [{
  value: '',
  text: `${t('common.choose')}`
}, ...countries.value]);

const cities = asyncComputed<SelectItem[]>(() => citiesList.value.map(({ placeName, postalCode }) => {
  const formattedPlaceName = formatPlaceName(placeName, postalCode);
  return {
    value: formattedPlaceName,
    text: formattedPlaceName
  };
}), [], { lazy: true });

const fields = computed(() => {
  const baseFields: InvoiceEditorFormField[] = [
    {
      name: 'company',
      width: '100%',
      type: 'text',
      label: `${t('invoice.address-data.form.company_USERTYPE')}`,
      textOptions: {
        maxLength: 40,
        hideCharsLeft: true
      }
    },
    {
      name: 'salutation',
      width: '33%',
      type: 'select',
      label: `${t('invoice.address-data.form.salutation_USERTYPE')}`,
      options: i18nToItemList('invoice.address-data.salutationOptions').value ?? [],
    },
    {
      name: 'firstname',
      width: '67%',
      type: 'text',
      label: `${t('invoice.address-data.form.firstname_USERTYPE')}`,
      textOptions: {
        maxLength: 40,
        hideCharsLeft: true
      }
    },
    {
      name: 'lastname',
      width: '100%',
      type: 'text',
      label: `${t('invoice.address-data.form.lastname_USERTYPE')}`,
      textOptions: {
        maxLength: 40,
        hideCharsLeft: true
      }
    },
    {
      name: 'countryIsoCode',
      width: '100%',
      type: 'select',
      options: countriesOptions.value,
      label: `${t('invoice.address-data.form.countryIsoCode_USERTYPE')}`
    },
    {
      name: 'zipAndCity',
      type: 'select',
      width: '100%',
      mode: 'input',
      options: cities.value,
      label: `${t('invoice.address-data.form.postalCodeAndCity_USERTYPE')}`,
      disabled: !addressDataVM.value?.countryIsoCode,
      showDisabledLabel: true,
    },
    {
      name: 'street',
      width: '75%',
      type: 'text',
      label: `${t('invoice.address-data.form.street_USERTYPE')}`,
      textOptions: {
        maxLength: 40,
        hideCharsLeft: true
      }
    },
    {
      name: 'houseNumber',
      width: '25%',
      type: 'text',
      label: `${t('invoice.address-data.form.houseNumber_USERTYPE')}`,
      textOptions: {
        maxLength: 4,
        hideCharsLeft: true
      }
    },
  ];
  const senderFields: InvoiceEditorFormField[] = [
    {
      name: 'phoneNumber',
      width: '100%',
      type: 'text',
      label: `${t('invoice.address-data.form.phoneNumber_USERTYPE')}`,
      textOptions: {
        maxLength: 20,
        hideCharsLeft: true
      }
    },
    {
      name: 'registerNumber',
      width: '100%',
      type: 'text',
      label: `${t('invoice.address-data.form.registerNumber_USERTYPE')}`,
      textOptions: {
        maxLength: 20,
        hideCharsLeft: true
      }
    },
    {
      name: 'registerEntity',
      width: '100%',
      type: 'text',
      label: `${t('invoice.address-data.form.registerEntity_USERTYPE')}`,
      textOptions: {
        maxLength: 40,
        hideCharsLeft: true
      }
    },
  ];
  const recipientFields: InvoiceEditorFormField[] = [
    {
      name: 'supplement',
      width: '100%',
      type: 'text',
      label: `${t('invoice.address-data.form.supplement_USERTYPE')}`,
      textOptions: {
        maxLength: 40,
        hideCharsLeft: true
      }
    },
  ];
  return [
    ...baseFields,
    ...(props.type === 'sender' ? senderFields : recipientFields)
  ];
});

/**
 * Extracts the postal code from a zipAndCity string and fetches the corresponding cities.
 * @param zipAndCity - The combined postal code and city string.
 */
async function extractAndFetchCity(zipAndCity: string) {
  if (zipAndCity.includes(', ')) {
    const [postalCode] = zipAndCity.split(', ');
    await getCitiesList(postalCode);
  } else await getCitiesList(zipAndCity);
}

/**
 * Validates if the zip code and city combination is correct.
 *
 * Fetches city data based on the input, checks for a valid match,
 * and updates the address view model and error messages accordingly.
 *
 * @returns Promise<boolean> - True if valid or empty, false otherwise.
 */
async function isValidZipAndCity(): Promise<boolean> {
  const zipAndCity = addressDataVM.value?.zipAndCity ?? '';
  await extractAndFetchCity(zipAndCity);
  const city = citiesList.value?.find(
    (item) => formatPlaceName(item.placeName, item.postalCode) === zipAndCity
  );

  const isValid = !!addressDataVM.value && (!!city || zipAndCity === '');
  if (addressDataVM.value) {
    addressDataVM.value.postalCode = isValid ? city?.postalCode ?? '' : '';
    addressDataVM.value.city = isValid ? city?.placeName ?? '' : '';
  }

  Vue.set(errorMessagesVM.value, 'zipAndCity', {
    isVisible: false,
    message: `${t('invoice.address-data.form.error.zipAndCity')}`
  });
  Vue.set(errorMessagesVM.value.zipAndCity, 'isVisible', !isValid);
  return isValid;
}

/**
 * Validates all inputs in the form.
 * @returns A boolean indicating whether all inputs are valid.
 */
async function validateInputs(): Promise<boolean> {
  return isValidZipAndCity();
}

watch(
  () => addressDataVM.value?.zipAndCity,
  async (newZipAndCity) => {
    if (errorMessagesVM.value.zipAndCity) Vue.set(errorMessagesVM.value.zipAndCity, 'isVisible', false);
    if (!addressDataVM.value.countryIsoCode || !newZipAndCity) {
      return;
    }
    await extractAndFetchCity(newZipAndCity);
  }, { immediate: true }
);

watch(
  () => addressDataVM.value?.countryIsoCode,
  (newCountryIsoCode, oldCountryIsoCode) => {
    if (newCountryIsoCode === oldCountryIsoCode) return;
    addressDataVM.value.zipAndCity = '';
  }
);

watch(
  () => addressDataVM.value,
  (newValue: ComponentAddressData, oldValue: ComponentAddressData) => {
    const changed = findChangedProps(newValue, oldValue);
    const key = changed[0];

    if (changed.length === 1 && errorMessagesVM.value[key]) {
      Vue.delete(errorMessagesVM.value, key);
    }
  }
);

expose<ValidateInputs>({ validateInputs });


return { __sfc: true,props, t, countries, getCitiesByPostCodeOrCity, findChangedProps, citiesList, addressDataVM, errorMessagesVM, getCitiesList, formatPlaceName, countriesOptions, cities, fields, extractAndFetchCity, isValidZipAndCity, validateInputs, BCXForm }
}

})